import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import { Users, Plus, Edit, Trash2, Fingerprint, ArrowLeft, Loader, ChevronDown, ChevronUp, Share2, Copy, Check, Mail, X, Link2, Send, Download, FileText } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import SessionTimer from "@/components/SessionTimer";

const ClientsPage = () => {
  const { user } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientFingerprints, setClientFingerprints] = useState({});
  const [shareModal, setShareModal] = useState(null); // { clientId, cardType, url, clientName }
  const [showEmailIntakeModal, setShowEmailIntakeModal] = useState(false);
  const [exportingEBTS, setExportingEBTS] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientFingerprints = async (clientId) => {
    try {
      const response = await axios.get(`/clients/${clientId}/fingerprints`);
      setClientFingerprints(prev => ({ ...prev, [clientId]: response.data }));
    } catch (error) {
      console.error("Failed to fetch fingerprints:", error);
    }
  };

  const toggleExpanded = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      if (!clientFingerprints[clientId]) {
        await fetchClientFingerprints(clientId);
      }
    }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`Are you sure you want to delete ${clientName}?`)) {
      return;
    }

    try {
      await axios.delete(`/clients/${clientId}`);
      toast.success(`${clientName} deleted successfully`);
      fetchClients();
    } catch (error) {
      console.error("Failed to delete client:", error);
      toast.error("Failed to delete client");
    }
  };

  const handleGenerateCard = async (clientId, clientName, cardType) => {
    const cardKey = `${clientId}-${cardType}`;
    setGeneratingCard(cardKey);
    setDownloadProgress(10);
    
    const progressToast = toast.loading(`Generating ${cardType.toUpperCase()} card...`);
    
    try {
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => Math.min(prev + 15, 90));
      }, 200);

      const response = await axios.post(
        `/fingerprint/generate-card/${cardType}?client_id=${clientId}`,
        {},
        { responseType: "blob" }
      );

      clearInterval(progressInterval);
      setDownloadProgress(100);

      const blob = response.data;
      const filename = `${cardType.toUpperCase()}_${clientName.replace(/ /g, "_")}.pdf`;
      
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: false
        });
        link.dispatchEvent(clickEvent);
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 200);
      }

      toast.success(`${cardType.toUpperCase()} card downloaded!`, { id: progressToast });
    } catch (error) {
      console.error("Failed to generate card:", error);
      
      let errorMsg = "Failed to generate card";
      if (error.response?.status === 404) {
        errorMsg = "No fingerprints found. Please capture fingerprints first.";
      } else if (error.response?.data) {
        try {
          const text = await error.response.data.text();
          const data = JSON.parse(text);
          errorMsg = data.detail || errorMsg;
        } catch (e) {
          console.error("Could not parse error:", e);
        }
      }
      
      toast.error(errorMsg, { id: progressToast });
    } finally {
      setGeneratingCard(null);
      setDownloadProgress(0);
    }
  };

  const handleCreateShareLink = async (client, cardType) => {
    const toastId = toast.loading(`Creating secure link...`);
    
    try {
      const response = await axios.post(
        `/secure-links/create?client_id=${client.id}&card_type=${cardType}`
      );
      
      toast.success("Secure link created!", { id: toastId });
      
      setShareModal({
        clientId: client.id,
        clientName: `${client.first_name} ${client.last_name}`,
        clientEmail: client.email || "",
        cardType: response.data.card_type,
        url: response.data.download_url,
        expiresAt: response.data.expires_at,
        expiresInDays: response.data.expires_in_days
      });
    } catch (error) {
      console.error("Failed to create share link:", error);
      const errorMsg = error.response?.data?.detail || "Failed to create share link";
      toast.error(errorMsg, { id: toastId });
    }
  };

  const handleExportEBTS = async (clientId, clientName, exportType) => {
    const toastId = toast.loading(`Generating EBTS ${exportType} file...`);
    setExportingEBTS(`${clientId}-${exportType}`);
    
    try {
      const endpoint = exportType === 'civil' 
        ? `/export/ebts/civil?client_id=${clientId}`
        : `/export/ebts/criminal?client_id=${clientId}`;
      
      const response = await axios.post(endpoint);
      const data = response.data;
      
      // Decode base64 and create download
      const binaryData = atob(data.ebts_data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/octet-stream' });
      
      // Download file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(
        `EBTS file exported: ${data.fingerprint_count} fingerprints`,
        { id: toastId }
      );
    } catch (error) {
      console.error("Failed to export EBTS:", error);
      const errorMsg = error.response?.data?.detail || "Failed to export EBTS file";
      toast.error(errorMsg, { id: toastId });
    } finally {
      setExportingEBTS(null);
    }
  };

  const fingerPositions = [
    { value: "right_thumb", label: "R-Thumb", short: "RT" },
    { value: "right_index", label: "R-Index", short: "RI" },
    { value: "right_middle", label: "R-Middle", short: "RM" },
    { value: "right_ring", label: "R-Ring", short: "RR" },
    { value: "right_pinky", label: "R-Pinky", short: "RP" },
    { value: "left_thumb", label: "L-Thumb", short: "LT" },
    { value: "left_index", label: "L-Index", short: "LI" },
    { value: "left_middle", label: "L-Middle", short: "LM" },
    { value: "left_ring", label: "L-Ring", short: "LR" },
    { value: "left_pinky", label: "L-Pinky", short: "LP" },
  ];

  const getEnrolledPositions = (clientId) => {
    const prints = clientFingerprints[clientId] || [];
    return prints.map(p => p.finger_position);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-center" />
      
      {/* Share Link Modal */}
      {shareModal && (
        <ShareLinkModal 
          data={shareModal} 
          onClose={() => setShareModal(null)} 
        />
      )}
      
      {/* Send Intake Email Modal */}
      {showEmailIntakeModal && (
        <SendIntakeEmailModal 
          onClose={() => setShowEmailIntakeModal(false)}
          onSuccess={() => fetchClients()}
        />
      )}
      
      {/* Header */}
      <header className="bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-slate-300 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-semibold">Client Management</h1>
          </div>
          <div className="flex items-center space-x-3">
            <SessionTimer enabled={true} />
            <button
              onClick={() => setShowEmailIntakeModal(true)}
              className="flex items-center space-x-2 bg-emerald-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-emerald-700 transition"
              data-testid="send-intake-email-btn"
            >
              <Mail className="w-4 h-4" />
              <span>Email Intake Link</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 transition"
              data-testid="add-client-btn"
            >
              <Plus className="w-4 h-4" />
              <span>Add Client</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-600 mx-auto"></div>
            <p className="mt-4 text-slate-600 text-sm">Loading clients...</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Clients Yet</h2>
            <p className="text-slate-600 text-sm mb-6">
              Start by adding your first client to begin managing their fingerprints.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-slate-800 text-white px-4 py-2 rounded font-medium hover:bg-slate-900 transition text-sm"
            >
              Add Your First Client
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div
                key={client.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      {client.first_name} {client.middle_name && client.middle_name !== 'NMN' ? `${client.middle_name.charAt(0)}. ` : ''}{client.last_name}
                    </h3>
                    {client.middle_name && (
                      <p className="text-xs text-slate-500">
                        {client.middle_name === 'NMN' ? '(No Middle Name)' : `Middle: ${client.middle_name}`}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="text-slate-400 hover:text-slate-600 p-1"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(client.id, `${client.first_name} ${client.last_name}`)
                      }
                      className="text-slate-400 hover:text-red-600 p-1"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-sm text-slate-600">
                  {client.email && (
                    <p className="truncate">{client.email}</p>
                  )}
                  {client.phone && (
                    <p>{client.phone}</p>
                  )}
                  {client.organization && (
                    <p className="text-slate-500 text-xs">{client.organization}</p>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => toggleExpanded(client.id)}
                      className="flex items-center space-x-1 text-xs text-slate-500 hover:text-slate-700"
                    >
                      <Fingerprint className="w-3.5 h-3.5" />
                      <span>{client.total_fingerprints || 0} prints</span>
                      {client.total_fingerprints > 0 && (
                        expandedClient === client.id ? 
                          <ChevronUp className="w-3.5 h-3.5" /> : 
                          <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      onClick={() => navigate(`/capture?client_id=${client.id}`)}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                    >
                      Capture →
                    </button>
                  </div>
                  
                  {/* Expanded Fingerprint Details */}
                  {expandedClient === client.id && client.total_fingerprints > 0 && (
                    <div className="mb-3 p-2 bg-slate-50 rounded">
                      <p className="text-xs font-medium text-slate-500 mb-1.5">Enrolled Fingers:</p>
                      <div className="flex flex-wrap gap-1">
                        {fingerPositions.map((pos) => {
                          const isEnrolled = getEnrolledPositions(client.id).includes(pos.value);
                          return (
                            <span
                              key={pos.value}
                              className={`px-2 py-1 text-xs rounded ${
                                isEnrolled
                                  ? "bg-blue-100 text-blue-700 font-medium"
                                  : "bg-gray-200 text-gray-400"
                              }`}
                              title={pos.label}
                            >
                              {pos.short}
                            </span>
                          );
                        })}
                      </div>
                      {clientFingerprints[client.id]?.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {clientFingerprints[client.id].map((fp, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>{fp.finger_position.replace(/_/g, ' ')}</span>
                              <span className={fp.compliance_status === 'passed' ? 'text-green-600' : 'text-yellow-600'}>
                                {fp.image_quality}% quality
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {client.total_fingerprints > 0 && (
                    <div className="space-y-2">
                      {/* Progress bar */}
                      {generatingCard?.startsWith(client.id) && downloadProgress > 0 && (
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${downloadProgress}%` }}
                          ></div>
                        </div>
                      )}
                      
                      {/* Download buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateCard(
                              client.id,
                              `${client.first_name}_${client.last_name}`,
                              "fd258"
                            );
                          }}
                          disabled={generatingCard !== null}
                          className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition flex items-center justify-center"
                          data-testid={`fd258-btn-${client.id}`}
                        >
                          {generatingCard === `${client.id}-fd258` ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            "FD-258"
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateCard(
                              client.id,
                              `${client.first_name}_${client.last_name}`,
                              "fd1164"
                            );
                          }}
                          disabled={generatingCard !== null}
                          className="flex-1 bg-purple-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50 transition flex items-center justify-center"
                          data-testid={`fd1164-btn-${client.id}`}
                        >
                          {generatingCard === `${client.id}-fd1164` ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            "FD-1164"
                          )}
                        </button>
                      </div>
                      
                      {/* Share link buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateShareLink(client, "fd258");
                          }}
                          className="flex-1 bg-slate-100 text-slate-700 px-3 py-2 rounded text-xs font-medium hover:bg-slate-200 transition flex items-center justify-center space-x-1"
                          data-testid={`share-fd258-btn-${client.id}`}
                        >
                          <Share2 className="w-3 h-3" />
                          <span>Share FD-258</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateShareLink(client, "fd1164");
                          }}
                          className="flex-1 bg-slate-100 text-slate-700 px-3 py-2 rounded text-xs font-medium hover:bg-slate-200 transition flex items-center justify-center space-x-1"
                          data-testid={`share-fd1164-btn-${client.id}`}
                        >
                          <Share2 className="w-3 h-3" />
                          <span>Share FD-1164</span>
                        </button>
                      </div>
                      
                      {/* EBTS Export buttons */}
                      <div className="flex space-x-2 pt-2 border-t border-slate-200 mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportEBTS(client.id, `${client.first_name}_${client.last_name}`, "civil");
                          }}
                          disabled={exportingEBTS !== null}
                          className="flex-1 bg-emerald-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition flex items-center justify-center space-x-1"
                          data-testid={`ebts-civil-btn-${client.id}`}
                        >
                          {exportingEBTS === `${client.id}-civil` ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3" />
                          )}
                          <span>EBTS Civil</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportEBTS(client.id, `${client.first_name}_${client.last_name}`, "criminal");
                          }}
                          disabled={exportingEBTS !== null}
                          className="flex-1 bg-orange-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-orange-700 disabled:opacity-50 transition flex items-center justify-center space-x-1"
                          data-testid={`ebts-criminal-btn-${client.id}`}
                        >
                          {exportingEBTS === `${client.id}-criminal` ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <FileText className="w-3 h-3" />
                          )}
                          <span>EBTS Criminal</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Client Modal */}
      {showForm && (
        <AddClientModal onClose={() => setShowForm(false)} onSuccess={fetchClients} />
      )}
    </div>
  );
};

// Share Link Modal Component
const ShareLinkModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [emailOpened, setEmailOpened] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    // Fetch user profile for email signature
    axios.get("/profile").then(res => {
      setProfile(res.data);
    }).catch(() => {});
  }, []);

  const handleCopy = async () => {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(data.url);
        setCopied(true);
        toast.success("Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.warn("Clipboard API failed, using fallback:", err);
      }
    }
    
    // Fallback: Create temporary textarea
    const textArea = document.createElement("textarea");
    textArea.value = data.url;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopied(true);
        toast.success("Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error("Failed to copy - please copy manually");
      }
    } catch (err) {
      toast.error("Failed to copy - please copy manually");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const generateMailtoLink = () => {
    const userName = profile?.full_name || "Your Fingerprint Service Provider";
    const userOrg = profile?.organization || "";
    const userEmail = profile?.email || "";
    
    const cardTypeDisplay = data.cardType === "FD258" ? "FD-258" : "FD-1164";
    const subject = `Your ${cardTypeDisplay} Fingerprint Card is Ready for Download`;
    
    const body = `Dear ${data.clientName},

Your ${cardTypeDisplay} fingerprint card is ready for download.

Download Link: ${data.url}

---

IMPORTANT INFORMATION:

• This is a SINGLE-USE link - it can only be downloaded ONE time
• The link will expire in 30 days from the date it was created
• Please save the file to your device immediately after downloading

Need a Duplicate Copy?
If you did not save your download, need a second copy, or have any issues accessing your document, please contact us directly and we will generate a new secure download link for you. Additional fees may apply for duplicate copies.

---

If you have any questions or need assistance, please don't hesitate to reach out.

Best regards,
${userName}${userOrg ? `\n${userOrg}` : ""}${userEmail ? `\n${userEmail}` : ""}

---
This message was sent via FingerprintVS.`;

    // We need to get the client's email - for now use a placeholder
    const clientEmail = data.clientEmail || "";
    return `mailto:${clientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleOpenEmailClient = () => {
    window.location.href = generateMailtoLink();
    setEmailOpened(true);
    toast.success("Email client opened - send when ready!");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900">Share Download Link</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-5 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
            <div className="flex items-center space-x-2 text-emerald-800 mb-2">
              <Check className="w-5 h-5" />
              <span className="font-medium">Secure link created!</span>
            </div>
            <p className="text-sm text-emerald-700">
              This link is for <strong>{data.clientName}</strong>'s <strong>{data.cardType}</strong> card.
            </p>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-xs text-amber-800">
              <strong>Important:</strong> This link expires in 30 days and can only be used once.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Download Link
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={data.url}
                readOnly
                className="flex-1 px-3 py-2 border border-slate-300 rounded bg-slate-50 text-sm"
              />
              <button
                onClick={handleCopy}
                className={`px-3 py-2 rounded font-medium transition flex items-center space-x-1 text-sm ${
                  copied 
                    ? "bg-emerald-600 text-white" 
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          {data.clientEmail && (
            <div className="space-y-2">
              <button
                onClick={handleOpenEmailClient}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded text-sm font-medium transition ${
                  emailOpened
                    ? "bg-white border-2 border-emerald-600 text-emerald-600"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
              >
                {emailOpened ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                <span>{emailOpened ? "Email Opened - Send in your email app" : `Open Email to ${data.clientEmail}`}</span>
              </button>
              <p className="text-xs text-slate-500 text-center">
                {emailOpened 
                  ? "Review and send the email in your email application"
                  : "Opens your default email app with the download link pre-filled"
                }
              </p>
            </div>
          )}
          
          {!data.clientEmail && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Add an email address to the client's profile to enable the "Open Email" feature.
              </p>
            </div>
          )}
        </div>
        
        <div className="p-5 border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full bg-slate-200 text-slate-700 px-4 py-2.5 rounded font-medium hover:bg-slate-300 transition text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// Send Intake Email Modal Component - Uses mailto: to open user's email client
const SendIntakeEmailModal = ({ onClose, onSuccess }) => {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [reason, setReason] = useState("Employment Background Check");
  const [generating, setGenerating] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailOpened, setEmailOpened] = useState(false);
  
  useEffect(() => {
    // Fetch user profile for email signature
    axios.get("/profile").then(res => {
      setProfile(res.data);
    }).catch(() => {});
  }, []);

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    
    if (!recipientEmail || !recipientName) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await axios.post("/intake/create", {
        reason: reason
      });
      
      setIntakeData({
        url: response.data.intake_url,
        expiresInDays: response.data.expires_in_days,
        recipientName,
        recipientEmail,
        reason
      });
      
      toast.success("Intake link created!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to create intake link";
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const generateMailtoLink = () => {
    if (!intakeData) return "";
    
    const userName = profile?.full_name || "Your Fingerprint Service Provider";
    const userOrg = profile?.organization || "";
    const userEmail = profile?.email || "";
    
    const subject = `Fingerprint Intake Form - ${intakeData.reason}`;
    
    const body = `Dear ${intakeData.recipientName},

You have been invited to complete a fingerprint intake form for: ${intakeData.reason}

Please click the link below to fill out your information:
${intakeData.url}

IMPORTANT:
• This link will expire in ${intakeData.expiresInDays} days
• Please complete the form before your appointment

If you have any questions or need assistance, please don't hesitate to reach out.

Best regards,
${userName}${userOrg ? `\n${userOrg}` : ""}${userEmail ? `\n${userEmail}` : ""}

---
This message was sent via FingerprintVS.`;

    return `mailto:${intakeData.recipientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCopyLink = async () => {
    if (!intakeData) return;
    
    try {
      await navigator.clipboard.writeText(intakeData.url);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = intakeData.url;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenEmailClient = () => {
    window.location.href = generateMailtoLink();
    setEmailOpened(true);
    toast.success("Email client opened - send when ready!");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900">
            {intakeData ? "Send Intake Link" : "Create Intake Link"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {!intakeData ? (
          <form onSubmit={handleGenerateLink} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="John Doe"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="client@example.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reason
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="Employment Background Check">Employment Background Check</option>
                <option value="Licensing">Licensing</option>
                <option value="Immigration">Immigration</option>
                <option value="Volunteer Screening">Volunteer Screening</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                type="submit"
                disabled={generating}
                className="flex-1 flex items-center justify-center space-x-2 bg-slate-800 text-white px-4 py-2 rounded text-sm font-medium hover:bg-slate-900 disabled:opacity-50 transition"
              >
                {generating ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    <span>Create Intake Link</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 rounded text-sm font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
              <div className="flex items-center space-x-2 text-emerald-800 mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Intake link created for {intakeData.recipientName}!</span>
              </div>
              <p className="text-sm text-emerald-700">
                Link expires in {intakeData.expiresInDays} days.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Intake Form Link
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={intakeData.url}
                  readOnly
                  className="flex-1 px-3 py-2 border border-slate-300 rounded bg-slate-50 text-sm"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-3 py-2 rounded font-medium transition flex items-center space-x-1 text-sm ${
                    copied 
                      ? "bg-emerald-600 text-white" 
                      : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <button
                onClick={handleOpenEmailClient}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded text-sm font-medium transition ${
                  emailOpened
                    ? "bg-white border-2 border-emerald-600 text-emerald-600"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
              >
                {emailOpened ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                <span>{emailOpened ? "Email Opened - Send in your email app" : `Open Email to ${intakeData.recipientEmail}`}</span>
              </button>
              <p className="text-xs text-slate-500 text-center">
                {emailOpened 
                  ? "Review and send the email in your email application"
                  : "Opens your default email app (Gmail, Outlook, etc.) with the message pre-filled"
                }
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-slate-300 rounded text-sm font-medium hover:bg-slate-50 transition"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AddClientModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    date_of_birth: "",
    sex: "",
    race: "",
    height: "",
    weight: "",
    eyes: "",
    hair: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    reason: "Employment Background Check",
    organization: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (!formData.middle_name.trim()) {
      newErrors.middle_name = "Middle name is required (enter NMN if no middle name)";
    }
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);

    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && key !== 'first_name' && key !== 'last_name' && key !== 'middle_name') {
          cleanedData[key] = null;
        }
      });
      
      await axios.post("/clients", cleanedData);
      toast.success("Client created successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to create client:", error);
      // Show more detailed error message
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const messages = error.response.data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
          toast.error(`Validation error: ${messages}`);
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error("Failed to create client");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Toaster position="top-right" />
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add New Client</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Fields */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                required
                value={formData.first_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.first_name ? 'border-red-500' : 'border-gray-300'
                }`}
                data-testid="client-first-name"
              />
              {errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Middle Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="middle_name"
                required
                placeholder="Enter NMN if no middle name"
                value={formData.middle_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.middle_name ? 'border-red-500' : 'border-gray-300'
                }`}
                data-testid="client-middle-name"
              />
              {errors.middle_name ? (
                <p className="text-red-500 text-xs mt-1">{errors.middle_name}</p>
              ) : (
                <p className="text-gray-500 text-xs mt-1">Enter "NMN" if no middle name</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                required
                value={formData.last_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.last_name ? 'border-red-500' : 'border-gray-300'
                }`}
                data-testid="client-last-name"
              />
              {errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Personal Details */}
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.date_of_birth ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sex</label>
              <select
                name="sex"
                value={formData.sex}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
              <input
                type="text"
                name="height"
                placeholder="5'10&quot;"
                value={formData.height}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
              <input
                type="text"
                name="weight"
                placeholder="180 lbs"
                value={formData.weight}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Additional Physical */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Race</label>
              <input
                type="text"
                name="race"
                value={formData.race}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Eye Color</label>
              <input
                type="text"
                name="eyes"
                value={formData.eyes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hair Color</label>
              <input
                type="text"
                name="hair"
                value={formData.hair}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Purpose */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Fingerprinting
              </label>
              <input
                type="text"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization/Employer
              </label>
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="create-client-btn"
            >
              {loading ? "Creating..." : "Create Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientsPage;
